import { createFileRoute, useRouter } from '@tanstack/react-router';
import { FormPageLayout } from '@/components/layouts';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { DEFAULT_INVOICE_LIST_FILTERS } from '@/components/organisms/invoices/InvoiceListPanel';
import { dateKeyInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { apiGet, operationsClient } from '@/lib/api';
import { arReportEndpoints, pnlReportEndpoints } from '@/lib/api/endpoints';
import { arReportKeys, pnlReportKeys } from '@/lib/queryKeys';
import { exportRowsAsXLSX } from '@/lib/export';
import {
  AnalyticsReportShell,
  ArClearingFootnote,
  BreakdownTable,
  MetricCard,
  ReportBarChart,
  ReportDateRangeControl,
  ReportLineChart,
  ReportSection,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  useDrillToList,
  useReportPeriod,
  type ScorecardColumn,
} from '@/components/organisms/analytics-reports';

interface ArOverview {
  totalOutstanding: number;
  current: number;
  overdue: number;
  dso: number;
  collected: number;
  openInvoices: number;
}
interface AgingRow {
  contactId: string;
  customerName: string;
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90plus: number;
  totalOutstanding: number;
}
interface AgingBuckets {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90plus: number;
}
interface JobWithoutPaymentRow {
  invoiceId: string;
  jobId: string;
  jobNumber: string;
  customerName: string;
  invoiceTotal: number;
  daysSinceCompleted: number;
}
interface DepositRow {
  method: string;
  count: number;
  amount: number;
}
interface ArSummary {
  period: { from: string; to: string };
  asOf: string;
  overview: ArOverview;
  overviewPrevious: { collected: number };
  agingByCustomer: AgingRow[];
  agingBuckets: AgingBuckets;
  jobsWithoutPayments: JobWithoutPaymentRow[];
  depositDetail: DepositRow[];
  wip: { activeJobs: number; wipValue: number };
  invoicedTrend: { period: string; value: number }[];
  collectedTrend: { period: string; value: number }[];
  clearingExcluded?: number | null;
}

// NOT-542 — Divisional P&L by Business Unit.
interface PnlRow {
  businessUnitId: string | null;
  businessUnitName: string;
  revenue: number;
  collected: number;
  cost: number;
  grossMargin: number;
  marginPct: number;
}
interface PnlReport {
  period: { from: string; to: string };
  rows: PnlRow[];
  totals: {
    revenue: number;
    collected: number;
    cost: number;
    grossMargin: number;
    marginPct: number;
  };
}

const sumBy = (rows: PnlRow[], pick: (r: PnlRow) => number): number =>
  rows.reduce((s, r) => s + pick(r), 0);

const pnlColumns: ScorecardColumn<PnlRow>[] = [
  { key: 'bu', header: 'Business Unit', type: 'text', value: (r) => r.businessUnitName },
  {
    key: 'revenue',
    header: 'Revenue',
    type: 'currency',
    value: (r) => r.revenue,
    total: (rows) => sumBy(rows, (r) => r.revenue),
  },
  {
    key: 'collected',
    header: 'Collected',
    type: 'currency',
    value: (r) => r.collected,
    total: (rows) => sumBy(rows, (r) => r.collected),
  },
  {
    key: 'cost',
    header: 'Cost',
    type: 'currency',
    value: (r) => r.cost,
    total: (rows) => sumBy(rows, (r) => r.cost),
  },
  {
    key: 'grossMargin',
    header: 'Gross Margin',
    type: 'currency',
    value: (r) => r.grossMargin,
    total: (rows) => sumBy(rows, (r) => r.grossMargin),
  },
  {
    key: 'marginPct',
    header: 'Margin %',
    type: 'percent',
    value: (r) => r.marginPct,
    // Margin % can't be summed — recompute it from the column totals.
    total: (rows) => {
      const revenue = sumBy(rows, (r) => r.revenue);
      return revenue ? sumBy(rows, (r) => r.grossMargin) / revenue : 0;
    },
  },
];

export const Route = createFileRoute('/_dashboard/dashboard/invoices/reports')({
  component: ArReportsRoute,
});

function ArReportsRoute() {
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  if (!pricingHidden) return <ArReportsPage />;

  return (
    <FormPageLayout
      title="Accounts Receivable"
      onBack={() =>
        void router.navigate({
          to: '/dashboard/invoices',
          search: DEFAULT_INVOICE_LIST_FILTERS,
        })
      }
    >
      <div className="mx-auto max-w-xl px-4 py-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="font-medium">Financial reports are hidden for your role.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Invoice status remains available from the Invoices list.
          </p>
        </div>
      </div>
    </FormPageLayout>
  );
}

const agingColumns: ScorecardColumn<AgingRow>[] = [
  { key: 'customer', header: 'Customer', type: 'text', value: (r) => r.customerName },
  {
    key: 'current',
    header: 'Current',
    type: 'currency',
    value: (r) => r.current,
    total: (rows) => rows.reduce((s, r) => s + r.current, 0),
  },
  {
    key: 'd1_30',
    header: '1–30',
    type: 'currency',
    value: (r) => r.d1_30,
    total: (rows) => rows.reduce((s, r) => s + r.d1_30, 0),
  },
  {
    key: 'd31_60',
    header: '31–60',
    type: 'currency',
    value: (r) => r.d31_60,
    total: (rows) => rows.reduce((s, r) => s + r.d31_60, 0),
  },
  {
    key: 'd61_90',
    header: '61–90',
    type: 'currency',
    value: (r) => r.d61_90,
    total: (rows) => rows.reduce((s, r) => s + r.d61_90, 0),
  },
  {
    key: 'd90plus',
    header: '90+',
    type: 'currency',
    value: (r) => r.d90plus,
    total: (rows) => rows.reduce((s, r) => s + r.d90plus, 0),
  },
  {
    key: 'total',
    header: 'Total Outstanding',
    type: 'currency',
    value: (r) => r.totalOutstanding,
    total: (rows) => rows.reduce((s, r) => s + r.totalOutstanding, 0),
  },
];

function ArReportsPage() {
  const period = useReportPeriod('analytics.ar.reportPeriod');
  const companyTz = useCompanyTimezone();
  const params = { from: period.from, to: period.to };
  const drillTo = useDrillToList();

  const q = useApiQuery<ArSummary>({
    queryKey: arReportKeys.report('summary', params),
    queryFn: () => apiGet<ArSummary>(operationsClient, arReportEndpoints.summary(params)),
    showErrorToast: false,
  });

  // NOT-542 — the divisional P&L is only meaningful once the tenant has business
  // units, so gate both the fetch and the section on there being ≥1 active unit.
  const buQuery = useBusinessUnits('active');
  const hasBusinessUnits = (buQuery.data?.length ?? 0) > 0;
  const pnlQuery = useApiQuery<PnlReport>({
    queryKey: pnlReportKeys.report('by-business-unit', params),
    queryFn: () => apiGet<PnlReport>(operationsClient, pnlReportEndpoints.byBusinessUnit(params)),
    enabled: hasBusinessUnits,
    showErrorToast: false,
  });

  const data = q.data;
  const o = data?.overview;
  const buckets = data?.agingBuckets;

  async function handleExport() {
    if (!data) return;
    const rows = data.agingByCustomer.map((r) => ({
      Customer: r.customerName,
      Current: r.current,
      '1-30': r.d1_30,
      '31-60': r.d31_60,
      '61-90': r.d61_90,
      '90+': r.d90plus,
      'Total Outstanding': r.totalOutstanding,
    }));
    const fname = `ar-report-${dateKeyInTimeZone(period.from, companyTz)}_${dateKeyInTimeZone(period.to, companyTz)}.xlsx`;
    await exportRowsAsXLSX(rows, fname, {
      currencyColumns: ['Current', '1-30', '31-60', '61-90', '90+', 'Total Outstanding'],
    });
  }

  return (
    <AnalyticsReportShell
      title="Accounts Receivable"
      subtitle="Collections & financial health"
      backTo="/dashboard/invoices"
      onExport={handleExport}
      exportDisabled={!data}
      actions={<ReportDateRangeControl period={period} />}
    >
      <ReportSection
        title="Overview"
        description="Aging is a snapshot as of today; Collected uses the selected range."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            title="Total Outstanding"
            value={o ? formatCurrency(o.totalOutstanding) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Current"
            value={o ? formatCurrency(o.current) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Overdue"
            value={o ? formatCurrency(o.overdue) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="DSO (days)"
            value={o ? o.dso.toFixed(1) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Collected"
            value={o ? formatCurrency(o.collected) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.collected, data?.overviewPrevious.collected)}
          />
          <MetricCard
            title="Open Invoices"
            value={o ? formatInteger(o.openInvoices) : '--'}
            isLoading={q.isLoading}
          />
        </div>
      </ReportSection>

      {hasBusinessUnits && (
        <ReportSection
          title="P&L by business unit"
          description="Revenue (accrual, at invoice issue) and Collected (cash applied) grouped by business unit, with actual job cost and gross margin. Invoices and jobs with no unit roll up to Unassigned."
        >
          <ScorecardTable<PnlRow>
            rows={pnlQuery.data?.rows ?? []}
            isLoading={pnlQuery.isLoading}
            emptyMessage="No divisional activity in this range"
            initialSortKey="revenue"
            columns={pnlColumns}
          />
        </ReportSection>
      )}

      <ReportSection title="AR aging by customer">
        <ScorecardTable<AgingRow>
          rows={data?.agingByCustomer ?? []}
          isLoading={q.isLoading}
          emptyMessage="No outstanding balances"
          initialSortKey="total"
          columns={agingColumns}
          onRowClick={(r) => drillTo('invoices', { q: r.customerName })}
        />
        <ArClearingFootnote amount={data?.clearingExcluded} />
      </ReportSection>

      <ReportSection title="Aging buckets">
        <ReportBarChart
          title="Outstanding by bucket"
          valueFormat="currency"
          isLoading={q.isLoading}
          data={
            buckets
              ? [
                  { label: 'Current', value: buckets.current },
                  { label: '1–30', value: buckets.d1_30 },
                  { label: '31–60', value: buckets.d31_60 },
                  { label: '61–90', value: buckets.d61_90 },
                  { label: '90+', value: buckets.d90plus },
                ]
              : []
          }
        />
        <ArClearingFootnote amount={data?.clearingExcluded} />
      </ReportSection>

      <ReportSection title="Jobs without payments">
        <BreakdownTable
          rows={data?.jobsWithoutPayments ?? []}
          isLoading={q.isLoading}
          emptyMessage="No completed jobs with unpaid invoices"
          columns={[
            { key: 'job', header: 'Job #', align: 'left', render: (r) => r.jobNumber },
            { key: 'customer', header: 'Customer', align: 'left', render: (r) => r.customerName },
            {
              key: 'total',
              header: 'Invoice Total',
              align: 'right',
              render: (r) => formatCurrency(r.invoiceTotal),
            },
            {
              key: 'days',
              header: 'Days Since Completed',
              align: 'right',
              render: (r) => formatInteger(r.daysSinceCompleted),
            },
          ]}
        />
      </ReportSection>

      <ReportSection title="Deposit detail">
        <BreakdownTable
          rows={data?.depositDetail ?? []}
          isLoading={q.isLoading}
          emptyMessage="No deposits in this range"
          columns={[
            { key: 'method', header: 'Method', align: 'left', render: (r) => r.method },
            {
              key: 'count',
              header: 'Count',
              align: 'right',
              render: (r) => formatInteger(r.count),
            },
            {
              key: 'amount',
              header: 'Amount',
              align: 'right',
              render: (r) => formatCurrency(r.amount),
            },
          ]}
        />
      </ReportSection>

      <ReportSection title="Work in progress">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Active Jobs"
            value={data ? formatInteger(data.wip.activeJobs) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="WIP Value"
            value={data ? formatCurrency(data.wip.wipValue) : '--'}
            isLoading={q.isLoading}
          />
        </div>
      </ReportSection>

      <ReportSection title="Collections trend">
        <div className="grid gap-4 lg:grid-cols-2">
          <ReportLineChart
            title="Invoiced by month"
            valueFormat="currency"
            isLoading={q.isLoading}
            points={(data?.invoicedTrend ?? []).map((p) => ({ period: p.period, value: p.value }))}
          />
          <ReportLineChart
            title="Collected by month"
            valueFormat="currency"
            isLoading={q.isLoading}
            points={(data?.collectedTrend ?? []).map((p) => ({ period: p.period, value: p.value }))}
          />
        </div>
      </ReportSection>
    </AnalyticsReportShell>
  );
}
